import { useCallback } from 'react';
import { useResumeSubscription, usePauseSubscription, useBulkCancelSubscription, useBulkUpdateBillingCycle } from '@/lib/api/subscriptions';
import toast from 'react-hot-toast';

export function useSubscriptionAction() {
    const bulkCancelSubscriptionMutation = useBulkCancelSubscription();
    const resumeSubscriptionMutation = useResumeSubscription();
    const pauseSubscriptionMutation = usePauseSubscription();

    const cancelSubscription = useCallback(
        (subscriptionId: string, cancelAtPeriodEnd = true) => {
            bulkCancelSubscriptionMutation.mutate(
                {
                    subscriptionIds: [subscriptionId],
                    cancelAtPeriodEnd,
                },
                {
                    onError: (error) => {
                        toast.error(`Failed to cancel subscription: ${error.message}`);
                    },
                },
            );
        },
        [bulkCancelSubscriptionMutation],
    );

    const resumeSubscription = useCallback(
        (subscriptionId: string) => {
            resumeSubscriptionMutation.mutate(
                {
                    subscriptionId,
                },
                {
                    onSuccess: () => {
                        toast.success('Subscription resumed successfully');
                    },
                    onError: (error) => {
                        toast.error(`Failed to resume subscription: ${error.message}`);
                    },
                },
            );
        },
        [resumeSubscriptionMutation],
    );

    const pauseSubscription = useCallback(
        (subscriptionId: string) => {
            pauseSubscriptionMutation.mutate(
                {
                    subscriptionId,
                },
                {
                    onSuccess: () => {
                        toast.success('Subscription paused successfully');
                    },
                    onError: (error) => {
                        toast.error(`Failed to pause subscription: ${error.message}`);
                    },
                },
            );
        },
        [pauseSubscriptionMutation],
    );

    return {
        cancelSubscription,
        resumeSubscription,
        pauseSubscription,
        isCanceling: bulkCancelSubscriptionMutation.isPending,
        isResuming: resumeSubscriptionMutation.isPending,
        isPausing: pauseSubscriptionMutation.isPending,
    };
}
